import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import { GlobalLoading } from './contexts/GlobalLoading';
import { SnackbarProvider } from './contexts/SnackbarProvider';
import axiosInstance from './utils/axiosInstance';
import ScrollToTop from './utils/ScrollToTop';
import Layout from './components/Layout';
import NotFoundPage from './pages/NotFoundPage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import OrderNewPage from './pages/OrderNewPage';
import OrdersCompletePage from './pages/OrdersCompletePage';
import ClientsPage from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import ClientNewPage from './pages/ClientNewPage';
import InventoryPage from './pages/InventoryPage';
import InventoryTransactionsPage from './pages/InventoryTransactionsPage';
import InventoryTransactionRegisterPage from './pages/InventoryTransactionRegisterPage';
import ProductDetailPage from './pages/ProductDetailPage';
import ProductNewPage from './pages/ProductNewPage';
import WarehousesPage from './pages/WarehousesPage';
import PickingPage from './pages/PickingPage';
import SettingsPage from './pages/SettingsPage';
import SettingCompanyPage from './pages/SettingCompanyPage';
import SettingInvoicePage from './pages/SettingInvoicePage';
import SalesDashboardPage from './pages/SalesDashboardPage';
import CategoryManagementPage from './pages/CategoryManagementPage';
import PickingDevicesPage from './pages/PickingDevicesPage';
import ChartPage from './pages/ChartPage';
import UsersPage from './pages/UsersPage';
import SetPasswordPage from './pages/SetPasswordPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import EstimatesPage from './pages/EstimatesPage';
import EstimateNewPage from './pages/EstimateNewPage';
import EstimateDetailPage from './pages/EstimateDetailPage';
import PurchasePage from './pages/PurchasePage';
import PurchaseNewPage from './pages/PurchaseNewPage';
import PurchaseDetailPage from './pages/PurchaseDetailPage';
//import ItemsPage from './pages/ItemsPage';
//import ItemDetailPage from './pages/ItemDetailPage';
import SuppliersPage from './pages/SuppliersPage';
import SupplierDetailPage from './pages/SupplierDetailPage';
//import ItemInventoryTransactionRegisterPage from './pages/ItemInventoryTransactionRegisterPage';
//import ItemInventoryTransactionsPage from './pages/ItemInventoryTransactionsPage';
import InvoicePage from './pages/InvoicePage';
//import BillingStatusPage from './pages/BillingStatusPage';
import ClientPricesPage from './pages/ClientPricesPage';
import ClientBillingPage from './pages/ClientBillingPage';
//import BillingClosingPage from './pages/BillingClosingPage';
//import BillingImmediatePage from './pages/BillingImmediatePage';
import BillingCreatePage from './pages/BillingCreatePage';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
//import IntegrationPage from './pages/IntegrationPage.jsx';
function App() {
  const [isAuth, setIsAuth] = useState(false);
  const [tenantLimits, setTenantLimits] = useState([]);
  const [userName, setUserName] = useState('ユーザ');
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const isPublicPage =
    location.pathname === '/login' ||
    location.pathname.startsWith('/set-password') ||
    location.pathname.startsWith('/forgot-password');
  useEffect(() => {
    if (isPublicPage) {
      setLoading(false);
      return;
    }
    const init = async () => {
      try {
        const userRes = await axiosInstance.get('/login/check-login');
        setUserName(userRes.data.user.username);
        setUserRole(userRes.data.user.role);
        setIsAuth(true);

        // 初回マウントでログイン済みなら tenantLimits も取得
        if (userRes.data.user) {
          const limitsRes = await axiosInstance.get('/tenant/limits');
          setTenantLimits(limitsRes.data);
        }

      } catch {
        setIsAuth(false);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [isPublicPage]);

  if (loading || !Array.isArray(tenantLimits)) {
    return <GlobalLoading />;
  }

  // 未ログインは /login にリダイレクト
  if (!isAuth && !isPublicPage) {
    localStorage.setItem('redirectAfterLogin', location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }

  // リミットがあるか
  const hasLimit = (key) =>
    Array.isArray(tenantLimits) && tenantLimits.some(l => l.key_name === key && l.option_value > 0);

  const getLimit = (key) => {
    const limit = tenantLimits.find(l => l.key_name === key);
    return limit ? Number(limit.option_value) : 0;
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline /> {/* リセットCSS */}
      <ScrollToTop />
      <GlobalLoading>
        {isPublicPage ? (
          <>
            {location.pathname === '/login' && (
              <LoginPage setUserName={setUserName} setIsAuth={setIsAuth} setTenantLimits={setTenantLimits} />
            )}
            {location.pathname.startsWith('/set-password') && <SetPasswordPage />}
            {location.pathname.startsWith('/forgot-password') && <ForgotPasswordPage />}
          </>
        ) : (
          <SnackbarProvider>
            <Routes>
              {/* ログイン後のページには共通レイアウトを適用 */}
              <Route element={<Layout userName={userName} isAuth={isAuth} theme={theme} hasLimit={hasLimit} />}>
                <Route path="/" element={<DashboardPage hasLimit={hasLimit} />} />
                {hasLimit('max_estimates') && (
                  <>
                    <Route path="/estimates" element={<EstimatesPage hasLimit={hasLimit} userRole={userRole} />} />
                    <Route path="/estimates/new" element={<EstimateNewPage hasLimit={hasLimit} />} />
                    <Route path="/estimates/:id" element={<EstimateDetailPage hasLimit={hasLimit} userRole={userRole} />} />
                  </>
                )}
                {hasLimit('max_purchase') && (
                  <>
                    <Route path="/purchases" element={<PurchasePage hasLimit={hasLimit} />} />
                    <Route path="/purchases/new" element={<PurchaseNewPage hasLimit={hasLimit} />} />
                    <Route path="/purchases/:id" element={<PurchaseDetailPage hasLimit={hasLimit} />} />
                  </>
                )}
                <Route path="/orders" element={<OrdersPage hasLimit={hasLimit} />} />
                <Route path="/orders/new" element={<OrderNewPage hasLimit={hasLimit} />} />
                <Route path="/orders/:id" element={<OrderDetailPage hasLimit={hasLimit} />} />
                <Route path="/orders/complete" element={<OrdersCompletePage hasLimit={hasLimit} />} />
                {hasLimit('max_clients') && (
                  <>
                    <Route path="/clients" element={<ClientsPage hasLimit={hasLimit} />} />
                    <Route path="/clients/new" element={<ClientNewPage hasLimit={hasLimit} />} />
                    <Route path="/clients/:id" element={<ClientDetailPage hasLimit={hasLimit} />} />
                    <Route path="/clients/:id/prices" element={<ClientPricesPage hasLimit={hasLimit} />} />
                    <Route path="/clients/:id/billing" element={<ClientBillingPage hasLimit={hasLimit} />} />
                  </>
                )}
                <Route path="/products" element={<InventoryPage hasLimit={hasLimit} getLimit={getLimit} />} />
                <Route path="/products/:id" element={<ProductDetailPage hasLimit={hasLimit} />} />
                <Route path="/products/new" element={<ProductNewPage hasLimit={hasLimit} />} />
                {/*
                <Route path="/items" element={<ItemsPage hasLimit={hasLimit} />} />
                <Route path="/items/:itemId" element={<ItemDetailPage hasLimit={hasLimit} />} />
                <Route path="/items/transactions/register" element={<ItemInventoryTransactionRegisterPage hasLimit={hasLimit} />} />
                <Route path="/items/transactions" element={<ItemInventoryTransactionsPage hasLimit={hasLimit} />} />
                */}
                <Route path="/suppliers" element={<SuppliersPage hasLimit={hasLimit} />} />
                <Route path="/suppliers/:supplierId" element={<SupplierDetailPage hasLimit={hasLimit} />} />
                <Route path="/inventory/transactions/register" element={<InventoryTransactionRegisterPage hasLimit={hasLimit} />} />
                <Route path="/inventory/transactions" element={<InventoryTransactionsPage hasLimit={hasLimit} />} />
                <Route path="/setting/warehouse" element={<WarehousesPage hasLimit={hasLimit} getLimit={getLimit} />} />
                <Route path="/picking" element={<PickingPage hasLimit={hasLimit} />} />
                {/*<Route path="/sales/billing" element={<BillingStatusPage hasLimit={hasLimit} getLimit={getLimit} />} />
                <Route path="/invoices/closing" element={<BillingClosingPage hasLimit={hasLimit} getLimit={getLimit} />} />
                <Route path="/invoices/immediate" element={<BillingImmediatePage hasLimit={hasLimit} getLimit={getLimit} />} />*/}
                <Route path="/invoices/create" element={<BillingCreatePage hasLimit={hasLimit} getLimit={getLimit} />} />
                <Route path="/invoices" element={<InvoicePage hasLimit={hasLimit} getLimit={getLimit} />} />
                <Route path="/invoices/:id" element={<InvoiceDetailPage hasLimit={hasLimit} />} />

                {hasLimit('max_picking_devices') && (
                  <Route path="/setting/picking-device" element={<PickingDevicesPage hasLimit={hasLimit} />} />
                )}
                <Route path="/setting" element={<SettingsPage hasLimit={hasLimit} />} />
                <Route path="/setting/users" element={<UsersPage getLimit={getLimit} />} />
                {hasLimit('ec') && (
                  <Route path="/setting/company" element={<SettingCompanyPage hasLimit={hasLimit} />} />
                )}
                <Route path="/setting/category" element={<CategoryManagementPage hasLimit={hasLimit} />} />
                {hasLimit('invoices_and_delivery') && (
                  <Route path="/setting/invoice" element={<SettingInvoicePage hasLimit={hasLimit} />} />
                )}
                {hasLimit('data_analysis') && (
                  <>
                    <Route path="/chart" element={<SalesDashboardPage hasLimit={hasLimit} />} />
                    <Route path="/chart/:chartSlug" element={<ChartPage hasLimit={hasLimit} />} />
                  </>
                )}
                {/*<Route path="/integrations" element={<IntegrationPage hasLimit={hasLimit} />} />*/}

                <Route path="/not-found" element={<NotFoundPage />} />
                <Route path="*" element={<Navigate to="/not-found" replace />} />
              </Route>
            </Routes>
          </SnackbarProvider>
        )}
      </GlobalLoading>
    </ThemeProvider>
  );
}
export default App;
